import { useQuery } from '@tanstack/react-query'
import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'

import { fetchHospital, hospitalQueryKey } from '../api/hospitals'
import type { HospitalListItem } from '../api/hospitals'
import { AboutTab } from '../components/profile/AboutTab'
import { BedsTab } from '../components/profile/BedsTab'
import { PricingTab } from '../components/profile/PricingTab'
import { SpecialistsTab } from '../components/profile/SpecialistsTab'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { Image } from '../components/Image'
import { ApiError } from '../lib/api'
import { hospitalPhotos } from '../lib/hospitalImages'
import { directionsUrl } from '../lib/hospitalProfile'

// The four profile sections (M12-T03, ADR-0027). Beds is the default.
const TABS = [
  { key: 'beds', label: 'Beds' },
  { key: 'specialists', label: 'Specialists' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'about', label: 'About' },
] as const
type TabKey = (typeof TABS)[number]['key']

/**
 * Hospital profile (M3-T08; retabbed M12-T03) at `/hospital/:slug`. Reads the
 * hospital from the public list endpoint and presents it in four tabs —
 * Beds · Specialists · Pricing · About — with the active tab in the URL
 * (`?tab=`) so it deep-links and survives refresh. Bed price lives only behind
 * a bed-type choice (Beds tab → `/book/:slug?type=…`). Loading, not-found, and
 * error states are explicit (CLAUDE.md).
 */
export function LocalProfile(): ReactNode {
  const { slug = '' } = useParams()
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: hospitalQueryKey(slug),
    queryFn: ({ signal }) => fetchHospital(slug, signal),
    retry: (count, err) =>
      err instanceof ApiError && err.code === 'not_found' ? false : count < 2,
  })

  const notFound =
    query.error instanceof ApiError && query.error.code === 'not_found'

  return (
    <PageShell measure={640}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <span className="text-[13px] font-medium text-ink-1">Hospital</span>
      </header>

      {query.isPending ? (
        <ProfileSkeleton />
      ) : notFound ? (
        <NotFound onBack={() => navigate('/')} />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : (
        <Profile hospital={query.data} />
      )}
    </PageShell>
  )
}

function Profile({ hospital }: { hospital: HospitalListItem }): ReactNode {
  const [params, setParams] = useSearchParams()
  const raw = params.get('tab')
  // ARRIVED FROM A SYMPTOM SEARCH (M33-T04 carried the slug here). The patient
  // has already said what is wrong, so the page opens on the doctors who answer
  // it rather than on beds.
  // `|| null` because `?specialty=` with an empty value is not an arrival
  // from a search; it is a truncated link, and `'' !== null` made the page act
  // on it.
  const carried = params.get('specialty') || null
  const active: TabKey = TABS.some((t) => t.key === raw)
    ? (raw as TabKey)
    : carried !== null
      ? 'specialists'
      : 'beds'
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const photos = hospitalPhotos(hospital.slug)

  // KEEPS EVERY OTHER PARAM. Passing an object to `setParams` REPLACES the whole
  // query string, so `?specialty=orthopaedics` vanished on the first tab tap —
  // or on an arrow key, which selects too. M33-T04 carries that specialty here
  // precisely so the page knows why the patient came.
  const select = (key: TabKey) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', key)
        return next
      },
      { replace: true },
    )

  // Automatic-activation tablist: arrows/Home/End move focus AND select. The
  // handler lives on the focused tab (ARIA APG pattern), not the tablist.
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const idx = TABS.findIndex((t) => t.key === active)
    let next: number
    if (e.key === 'ArrowRight') next = (idx + 1) % TABS.length
    else if (e.key === 'ArrowLeft') next = (idx - 1 + TABS.length) % TABS.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = TABS.length - 1
    else return
    e.preventDefault()
    const target = TABS[next]
    if (!target) return
    select(target.key)
    tabRefs.current[next]?.focus()
  }

  return (
    <>
      <div className="flex flex-1 flex-col overflow-y-auto">
        {/* M17-T19 — the photo mosaic. T18b put the lead alone in an h-32 band,
            which cropped a 3:2 photograph to a 128px strip and showed almost
            nothing of the building. The frame
            (designs/screens-figma-profile.jsx FgGallery, "Britannia Medical
            Centre Container") draws five: the lead down the left at 410px, and
            four supporting photographs in a 2x2 beside it at 201px each —
            2*201 + 8px gap = 410, which is why those numbers are exact rather
            than approximate. */}
        {photos ? (
          <ProfileGallery photos={photos} />
        ) : (
          <div
            className="grid h-[220px] place-items-center bg-bg-sunken font-mono text-[11px] text-ink-3 sm:h-[410px]"
            aria-hidden="true"
          >
            hospital photo
          </div>
        )}

        <PageBand
          className="px-4 pt-3"
          contentClassName="flex items-start justify-between gap-2"
        >
          <div className="min-w-0">
            {/* PORTED — the profile header sets the name in the display face
                at --fs-d-xs and the meta line at --fs-md on --ink-2
                (screens-figma-profile.jsx ~183). The incumbent was text-xl
                bold with an 12px meta line on --ink-3. */}
            <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
              {hospital.name}
            </h1>
            <p className="mt-1 inline-flex items-center gap-1 text-[16px] leading-6 text-ink-2">
              <Icon name="pin" size={16} /> {hospital.city}
              {hospital.distance_km != null && (
                <span className="tabular-nums">
                  {' '}
                  · {hospital.distance_km} km
                </span>
              )}
            </p>
          </div>
          <button
            type="button"
            aria-label={`Save ${hospital.name}`}
            className="rounded-full p-1 text-ink-3 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Icon name="heart" size={20} />
          </button>
        </PageBand>

        <PageBand
          className="mt-3 border-b border-line-1 px-2"
          contentClassName="flex"
          role="tablist"
          aria-label="Hospital sections"
        >
          {TABS.map((t, i) => {
            const selected = active === t.key
            return (
              <button
                key={t.key}
                ref={(el) => {
                  tabRefs.current[i] = el
                }}
                type="button"
                role="tab"
                id={`tab-${t.key}`}
                aria-selected={selected}
                aria-controls={`panel-${t.key}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => select(t.key)}
                onKeyDown={onKeyDown}
                // PORTED — tab labels are --fs-sm at SemiBold upstream
                // (screens-figma-profile.jsx ~79). The selected tab keeps the
                // accent underline: weight alone is too weak a signal, and
                // after T02 the accent no longer separates on colour in
                // greyscale (M17-T06), so the rule carries the state.
                className={`flex-1 border-b-2 px-1 py-2.5 text-[14px] leading-5 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent ${
                  selected
                    ? 'border-accent font-semibold text-ink-1'
                    : 'border-transparent font-normal text-ink-3 hover:text-ink-1'
                }`}
              >
                {t.label}
              </button>
            )
          })}
        </PageBand>

        <div
          role="tabpanel"
          id={`panel-${active}`}
          aria-labelledby={`tab-${active}`}
          tabIndex={0}
          className="pb-4 focus-visible:outline-none"
        >
          <PageBand>
            {active === 'beds' && <BedsTab hospital={hospital} />}
            {active === 'specialists' && (
              <SpecialistsTab slug={hospital.slug} specialty={carried} />
            )}
            {active === 'pricing' && <PricingTab slug={hospital.slug} />}
            {active === 'about' && <AboutTab hospital={hospital} />}
          </PageBand>
        </div>
      </div>

      <PageBand
        className="sticky bottom-0 border-t border-line-1 bg-bg-app p-3"
        contentClassName="flex gap-2"
      >
        {/* PRIMARY ONLY WHEN BOOK IS NOT ON SCREEN (M33-T05, ADR-0062).
          Branching on `carried` was wrong twice over: a symptom arrival that
          moved to About had NO primary action at all and two identical
          secondary buttons, and a procedure arrival — which carries no
          specialty, because `x-ray` resolves an unstaffed Radiology — kept the
          loud treatment. The Specialists tab owns the primary action, because
          that is where Book lives.

          `bg-sos` IS GONE FROM THIS FILE. It is the emergency red, reserved for
          the SOS journey and ADR-0035's call-ahead flow — and nothing routes
          that journey here: `Emergency.tsx` never links to a hospital profile.
          So it was only ever the loudest control on a routine page. */}
        <a
          href={`tel:${hospital.phone}`}
          className={
            active === 'specialists'
              ? 'flex flex-1 items-center justify-center gap-1.5 rounded-r3 border border-line-1 px-4 py-2.5 text-sm font-medium text-ink-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
              : 'flex flex-1 items-center justify-center gap-1.5 rounded-r3 bg-accent px-4 py-2.5 text-sm font-medium text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
          }
        >
          <Icon name="phone" size={16} /> Call hospital
        </a>
        <a
          href={directionsUrl(hospital.lat, hospital.lng)}
          target="_blank"
          rel="noreferrer"
          className="flex flex-1 items-center justify-center gap-1.5 rounded-r3 border border-line-1 px-4 py-2.5 text-sm font-medium text-ink-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="arrow" size={16} /> Directions
        </a>
      </PageBand>
    </>
  )
}

/**
 * The five-photograph mosaic (M17-T19), as the frame draws it: the lead down
 * the left, four supporting photographs in a 2x2 beside it.
 *
 * **The grid never has a hole.** Eight of the ten partners supply exactly four
 * supporting photographs, but SMSH and Sweden Ghana supply three — not by
 * accident, but because T18b withheld one apiece (a third-party watermark, and
 * an identifiable patient needing consent). The Bank supplies five. So the
 * count is 3, 4 or 5 today and could be anything tomorrow, and the layout
 * places what exists rather than padding with blanks or repeats:
 *
 * - 4 or more — a full 2x2; a fifth is not rendered, since the frame draws four
 * - 3 — two across the top, the third spanning the width beneath them
 * - 2 — each spanning the width, stacked
 * - 1 — filling the block
 * - 0 — no block at all, and the lead takes the full width
 *
 * **Deviation from the frame, deliberate:** it renders the four only on
 * desktop (`!mobile`). Local is a mobile-first interface, so that would hide
 * the extra photographs from most of the people this page is for — which is
 * the opposite of why they were supplied. They render on mobile too, as a 2x2
 * beneath the lead. Reverting to frame-exact is deleting the mobile heights.
 */
function ProfileGallery({
  photos,
}: {
  photos: NonNullable<ReturnType<typeof hospitalPhotos>>
}): ReactNode {
  const supporting = photos.gallery.slice(0, 4)

  // Which cells an image occupies, by how many there are. Keyed by count so the
  // rule reads as the table in the doc comment above.
  const span = (index: number): string => {
    const n = supporting.length
    if (n === 1) return 'col-span-2 row-span-2'
    if (n === 2) return 'col-span-2'
    if (n === 3 && index === 2) return 'col-span-2'
    return ''
  }

  return (
    // The frame draws this gallery across the full page width
    // (screens-figma-profile.jsx:199, padding `0 --s-12 --s-12`), and it now
    // does — this is M17-T19 / M20 finding F1.
    //
    // `sm:grid-rows-1` is the whole fix, and it is load-bearing. `Image` sets an
    // inline `aspect-ratio` on its frame, so with the row track left implicit
    // (`auto`) the track sized to the lead photo's ASPECT rather than to the
    // band: on the 316px column the lead came out 410px and fitted, but widened
    // to 692.5px it grew to 447.8px, overflowed the pinned `sm:h-[410px]` band
    // and struck through the hospital name below. `grid-rows-1` is
    // `minmax(0, 1fr)`, which makes the track DEFINITE against the band's
    // height — both dimensions definite, so the inline aspect-ratio no longer
    // sizes anything. Measured at 1440: lead 447.8px -> 410px at the same
    // 692.5px width, and the band no longer overflows at any width.
    //
    // Do not swap `grid-rows-1` for a bare row count or drop the pinned height
    // without re-measuring at 1440 — a green test suite does not see this, and
    // did not: the defect shipped under a passing profile suite.
    <div
      className={`grid gap-2 px-4 pt-3 sm:h-[410px] sm:grid-cols-2 sm:grid-rows-1 ${PAGE_GUTTER_DESKTOP}`}
    >
      <Image
        asset={photos.main.asset}
        alt={photos.main.alt}
        eager
        className="h-[220px] w-full rounded-r3 sm:h-full"
      />
      {supporting.length > 0 && (
        <div className="grid grid-cols-2 grid-rows-2 gap-2 sm:h-full">
          {supporting.map((photo, i) => (
            <Image
              key={photo.asset.webp}
              asset={photo.asset}
              alt={photo.alt}
              className={`h-[106px] w-full rounded-r3 sm:h-full ${span(i)}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ProfileSkeleton(): ReactNode {
  return (
    <div aria-hidden="true" className="flex flex-1 flex-col gap-3 p-4">
      <div className="h-[220px] animate-pulse rounded-r3 bg-bg-sunken sm:h-[410px]" />
      <div className="h-6 w-2/3 animate-pulse rounded bg-bg-sunken" />
      <div className="grid grid-cols-2 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 animate-pulse rounded-r3 bg-bg-sunken" />
        ))}
      </div>
    </div>
  )
}

function NotFound({ onBack }: { onBack: () => void }): ReactNode {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-1 px-5 py-16 text-center">
      <h2 className="text-base font-semibold text-ink-1">Hospital not found</h2>
      <p className="text-[13px] text-ink-3">
        That hospital isn&apos;t in our list.
      </p>
      <button
        type="button"
        onClick={onBack}
        className="mt-3 rounded-full border border-line-2 bg-bg-app px-4 py-1.5 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Back to hospitals
      </button>
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <div
      role="alert"
      className="flex flex-1 flex-col items-center justify-center gap-1 px-5 py-16 text-center"
    >
      <h2 className="text-base font-semibold text-ink-1">
        Couldn&apos;t load this hospital
      </h2>
      <p className="text-[13px] text-ink-3">
        Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-full bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Retry
      </button>
    </div>
  )
}
